import type { Variation } from '@gonfalon/flags/types';
import { isArray, isBoolean, isEmpty, isEqual, isNil, isNumber, isPlainObject, isString } from 'lodash';

export enum ValueType {
  BOOLEAN = 'boolean',
  NUMBER = 'number',
  STRING = 'string',
  JSON = 'json',
}

export function isTypedArray<T>(v: unknown, check: (v: unknown) => v is T): v is T[] {
  if (!Array.isArray(v)) {
    return false;
  }

  for (const item of v) {
    if (!check(item)) {
      return false;
    }
  }

  return true;
}

export function isStringArray(value: unknown): value is string[] {
  return isTypedArray(value, isString);
}

export function isValueTypeString(v: unknown): v is ValueType {
  return v === ValueType.BOOLEAN || v === ValueType.JSON || v === ValueType.NUMBER || v === ValueType.STRING;
}

export function nilFilter<T>(v: T | undefined): v is T {
  return v !== undefined;
}

export const isJSONValue = (v?: unknown) => isPlainObject(v) || isArray(v);

export const couldBeJSONValue = (v?: unknown) => {
  if (isJSONValue(v)) {
    return true;
  }
  if (isString(v)) {
    if (v === '') {
      return false;
    }
    try {
      JSON.parse(v);
      return true;
    } catch {
      return false;
    }
  }
  return false;
};

export const couldBeNumber = (v?: unknown) => {
  if (isNil(v) || v === '') {
    return false;
  }
  if (isString(v) && v.endsWith('.')) {
    return false;
  }
  const num = Number(v);
  return !isNaN(num) && Number.isFinite(num);
};

export const couldBeNumerical = (v: unknown) =>
  couldBeNumber(v) && !((v as string)?.endsWith && (v as string).endsWith('.'));

export const couldBeBoolean = (v?: unknown) => isBoolean(v) || v === 'true' || v === 'false' || v === '0' || v === '1';

export const areAllBooleans = (values: unknown[]) => !!values.length && values.every(isBoolean);

export const areAllNumbers = (values: unknown[]) => !!values.length && values.every(isNumber);

export const areAllStrings = (values: unknown[]) => !!values.length && values.every(isString);

export const areAllJSON = (values: unknown[]) => !!values.length && values.every(isJSONValue);

export const couldAllBeNumbers = (values: unknown[]) => values.every(couldBeNumber);

export const couldAllBeBooleans = (values: unknown[]) => values.every(couldBeBoolean);

export const couldAllBeJSON = (values: unknown[]) => values.every(couldBeJSONValue);

export function getAlternateType(v: unknown) {
  const type = typeof v;
  if (type !== 'string') {
    return 'string';
  }
  if (couldBeNumber(v)) {
    return 'number';
  }
  if (couldBeBoolean(v)) {
    return 'boolean';
  }
}

export function stringifyValue(val?: unknown): string {
  if (isNil(val)) {
    return 'null';
  }
  return isJSONValue(val) ? JSON.stringify(val) : val.toString();
}

export function coerceToType(value: unknown, newType?: 'number' | 'string' | 'boolean' | 'json') {
  if (typeof value === newType) {
    return value;
  }
  if (newType === 'string') {
    return stringifyValue(value);
  } else if (newType === 'number') {
    return couldBeNumber(value) ? Number(value) : value;
  } else if (newType === 'boolean') {
    return couldBeBoolean(value) ? value === 'true' : value;
  } else if (newType === 'json' && isString(value)) {
    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  }
  return value;
}

export const isEmptyValue = (value: unknown) => !isBoolean(value) && !couldBeNumber(value) && isEmpty(value);

export function inferType(values: unknown[]) {
  const vs: string[] = values
    .filter((value) => !isEmptyValue(value))
    .filter((value) => !(value instanceof Object && value.constructor === Object)) // Filter out plain objects
    .map((value) => {
      // Try to convert to string, but handle edge cases
      if (typeof value === 'object' && value !== null && 'toString' in value && typeof value.toString === 'function') {
        return value.toString();
      }
      return String(value);
    });

  if (vs.length === 0) {
    return ValueType.STRING;
  }

  const booleans = new Set([true, false]);

  const asBooleans: Set<boolean> = new Set(
    vs
      .map((v) => {
        switch (v) {
          case 'true':
            return true;
          case 'false':
            return false;
          default:
            return undefined;
        }
      })
      .filter(nilFilter),
  );

  if (isEqual(new Set(vs), booleans) || isEqual(asBooleans, booleans)) {
    return ValueType.BOOLEAN;
  }

  if (vs.every(couldBeNumerical)) {
    return ValueType.NUMBER;
  }

  if (couldAllBeJSON(vs)) {
    return ValueType.JSON;
  }

  return ValueType.STRING;
}

export function isHomogeneousArray(values: unknown[]) {
  const vs = values.filter((v) => !isEmptyValue(v));
  return vs.every(isString) || vs.every(Number.isFinite) || vs.every(isJSONValue) || vs.every(isBoolean);
}

export function detectType(values: unknown[]) {
  const vs = values.filter((value) => !isEmptyValue(value)).map((value) => value);

  if (!vs.length) {
    return ValueType.STRING;
  }

  if (areAllBooleans(vs)) {
    return ValueType.BOOLEAN;
  }

  if (areAllNumbers(vs)) {
    return ValueType.NUMBER;
  }

  if (areAllJSON(vs)) {
    return ValueType.JSON;
  }

  return ValueType.STRING;
}

/**
 * Converts FormVariation[] to Variation[] format for VariationPicker component
 */
export function convertFormVariationsToVariations(
  formVariations: Array<{ name: string; value: unknown; _id?: string }>,
): Variation[] {
  return formVariations.map((v, index) => ({
    _id: v._id || `var-${index}`,
    name: v.name,
    value: v.value,
    description: '',
  }));
}
